import { UserChip } from './user-chip'

/**
 * Render a tool-result's full data payload as labeled rows, suitable for the
 * "View details" expansion on a ToolCallCard. Built to replace the prior
 * `JSON.stringify` <pre> view which (a) exposed `<untrusted>…</untrusted>`
 * tags that the LLM should see but operators shouldn't and (b) was JSON,
 * which is the wrong shape for non-engineer support staff.
 *
 * Conventions:
 *   - Strings → strip `<untrusted>` markers, then display.
 *   - Numbers → tabular-nums.
 *   - Booleans → "Yes" / "No" pills with green/neutral tone.
 *   - null / undefined / empty string → muted em-dash.
 *   - Arrays → numbered items; each item's children indent.
 *   - Objects → "Label: value" rows; nested objects/arrays indent one level.
 *   - Field labels humanized via override map + snake-case → Title Case.
 *   - Internal/no-signal fields hidden (`truncated`, etc.).
 *
 * Special detection: if an object has a numeric `user_id` AND any of
 * (`name`, `first_name`, `email`), the user-id field renders as a
 * `<UserChip>` that opens the existing user modal. Keeps the chat drawer
 * mounted underneath so the operator can come back to the conversation.
 */

interface StructuredDataViewProps {
  data: unknown
  /** Field keys to hide. Defaults to a small list of internal/no-signal keys
   *  (truncated, etc.). Pass your own set to override. */
  hideKeys?: Set<string>
}

const DEFAULT_HIDE_KEYS = new Set<string>([
  'truncated', // operators don't need to see "matches were truncated" as a separate field
])

// Override snake-case → Title Case for keys we want to render specifically.
// Anything not in this map falls back to the generic humanizer.
const LABEL_OVERRIDES: Record<string, string> = {
  user_id: 'User',
  vendor_id: 'Vendor',
  vendor_event_id: 'Vendor-event',
  item_id: 'Item',
  order_id: 'Order',
  order_display_id: 'Order ID',
  transaction_log_id: 'Transaction',
  pending_action_id: 'Pending action',
  voucher_id: 'Voucher',
  event_id: 'Event',
  vendor_name: 'Vendor',
  first_name: 'First name',
  last_name: 'Last name',
  phone_number: 'Phone',
  is_available: 'Available',
  is_direct_pay: 'Direct pay',
  is_vegetarian: 'Vegetarian',
  is_vegan: 'Vegan',
  is_glutenfree: 'Gluten-free',
  is_dairyfree: 'Dairy-free',
  is_beverage: 'Beverage',
  is_alcoholic: 'Alcoholic',
  is_active: 'Active',
  validated_email: 'Email verified',
  hide_voucher_name: 'Name hidden from attendees',
  match_count: 'Matches',
  total_matches: 'Total matches',
  event_orders: 'Event orders',
  event_transactions: 'Event txns',
  order_count: 'Orders',
  tokens_spent: 'Tokens spent',
  voucher_value: 'Voucher value',
  voucher_type: 'Voucher type',
  voucher_name: 'Voucher',
  ambiguous_count: 'Ambiguous count',
  matched_by: 'Match source',
  max_uses: 'Max uses',
  max_uses_per_user: 'Max uses per user',
  message_count: 'Messages',
  created_at: 'Created',
  completed_at: 'Completed',
  claimed_at: 'Claimed',
  started_at: 'Started',
  ended_at: 'Ended',
  expires_at: 'Expires',
  first_seen_at: 'First seen',
  timestamp: 'Time',
  organization_name: 'Organization',
  resolution_status: 'Resolution',
  classification: 'Category',
}

const UNTRUSTED_PATTERN = /<\/?\s*untrusted\s*>/gi
function strip(value: string): string {
  return value.replace(UNTRUSTED_PATTERN, '').trim()
}

function humanize(key: string): string {
  if (key in LABEL_OVERRIDES) return LABEL_OVERRIDES[key]
  if (!key) return ''
  const cleaned = key.replace(/^is_/, '') // bool prefix is implicit
  return cleaned
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w, i) =>
      i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w.toLowerCase(),
    )
    .join(' ')
}

const TIMESTAMP_KEYS = new Set<string>([
  'created_at',
  'completed_at',
  'claimed_at',
  'started_at',
  'ended_at',
  'expires_at',
  'first_seen_at',
  'timestamp',
])

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function StructuredDataView({ data, hideKeys = DEFAULT_HIDE_KEYS }: StructuredDataViewProps) {
  if (data === null || data === undefined) {
    return <Empty />
  }
  if (typeof data !== 'object') {
    // Primitive at the root — render it solo.
    return <Leaf value={data} />
  }
  if (Array.isArray(data)) {
    return <ArrayList items={data} hideKeys={hideKeys} />
  }
  return <ObjectRows data={data as Record<string, unknown>} hideKeys={hideKeys} />
}

function ObjectRows({
  data,
  hideKeys,
}: {
  data: Record<string, unknown>
  hideKeys: Set<string>
}) {
  // Detect a "user record" shape — present user_id as a clickable chip
  // INSTEAD of a plain number when the object also carries a name/email.
  // Falls back to standard rendering for non-user shapes.
  const userId = typeof data.user_id === 'number' ? data.user_id : null
  const userName =
    typeof data.name === 'string' ? strip(data.name) :
    typeof data.first_name === 'string' || typeof data.last_name === 'string'
      ? `${typeof data.first_name === 'string' ? strip(data.first_name) : ''} ${typeof data.last_name === 'string' ? strip(data.last_name) : ''}`.trim()
      : ''
  const hasUserShape = userId !== null && (userName.length > 0 || typeof data.email === 'string')

  const entries = Object.entries(data).filter(([k]) => !hideKeys.has(k))

  return (
    <dl className="space-y-0.5">
      {entries.map(([k, v]) => {
        // The user_id field on a user-shaped object renders as the chip.
        if (hasUserShape && k === 'user_id' && typeof v === 'number') {
          return (
            <FieldRow key={k} label={humanize(k)}>
              <UserChip userId={v} name={userName} variant="block" />
            </FieldRow>
          )
        }
        return (
          <FieldRow key={k} label={humanize(k)}>
            <Value value={v} keyHint={k} hideKeys={hideKeys} />
          </FieldRow>
        )
      })}
    </dl>
  )
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-[11px] leading-relaxed">
      <dt className="w-32 shrink-0 text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className="min-w-0 flex-1 text-neutral-800 dark:text-neutral-100">{children}</dd>
    </div>
  )
}

function Value({
  value,
  keyHint,
  hideKeys,
}: {
  value: unknown
  keyHint?: string
  hideKeys: Set<string>
}) {
  if (value === null || value === undefined) return <Empty />
  if (typeof value === 'string') {
    const cleaned = strip(value)
    if (!cleaned) return <Empty />
    if (keyHint && TIMESTAMP_KEYS.has(keyHint)) {
      return <span>{formatTimestamp(cleaned)}</span>
    }
    return <span className="break-words">{cleaned}</span>
  }
  if (typeof value === 'number') {
    return <span className="tabular-nums">{value.toLocaleString()}</span>
  }
  if (typeof value === 'boolean') {
    return (
      <span
        className={
          value
            ? 'inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
            : 'inline-flex items-center rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
        }
      >
        {value ? 'Yes' : 'No'}
      </span>
    )
  }
  if (Array.isArray(value)) {
    return <ArrayList items={value} hideKeys={hideKeys} indented />
  }
  if (typeof value === 'object') {
    return (
      <div className="ml-1 border-l border-neutral-200 pl-2 dark:border-neutral-700">
        <ObjectRows data={value as Record<string, unknown>} hideKeys={hideKeys} />
      </div>
    )
  }
  return <span>{String(value)}</span>
}

function Leaf({ value }: { value: unknown }) {
  return (
    <div className="text-[11px] text-neutral-800 dark:text-neutral-100">
      <Value value={value} hideKeys={DEFAULT_HIDE_KEYS} />
    </div>
  )
}

function Empty() {
  return <span className="text-neutral-400 italic dark:text-neutral-500">—</span>
}

function ArrayList({
  items,
  hideKeys,
  indented,
}: {
  items: unknown[]
  hideKeys: Set<string>
  indented?: boolean
}) {
  if (items.length === 0) {
    return <span className="text-neutral-400 italic dark:text-neutral-500">(none)</span>
  }
  return (
    <ol
      className={
        indented
          ? 'mt-1 ml-1 space-y-1.5 border-l border-neutral-200 pl-2 dark:border-neutral-700'
          : 'space-y-1.5'
      }
    >
      {items.map((item, i) => (
        <li key={i} className="space-y-0.5">
          <div className="text-[10px] font-medium text-neutral-400 dark:text-neutral-500">
            #{i + 1}
          </div>
          <Value value={item} hideKeys={hideKeys} />
        </li>
      ))}
    </ol>
  )
}
